/**
 * Die Zeilen der Kachel „Return Shipping Costs" (B2, Owner-Auftrag 2026-09-17).
 *
 * 🔴 EIGENE Datei, weil die vier Töpfe seit B2 vier VERSCHIEDENE Aussagen
 * treffen und die Trennung der ganze Zweck der Änderung ist:
 *
 *   `customer_paid_count`  ein echter Abzug (> 0)
 *   `free_return_count`    dem Kunden ZUGESAGT kostenfrei (= 0)
 *   `undetermined_count`   kein bestimmter Betrag (NULL)
 *
 * Vorher lagen `= 0` und `NULL` in einem Topf, und die Kachel behauptete damit
 * eine Rechenart, die das Backend nicht ausführt.
 *
 * Reine Funktion — kein React. Damit ist prüfbar, dass die zwei neuen Zeilen
 * **verschiedene** Zahlen führen; eingebettet in die Seite wäre das nur über
 * einen Browserlauf zu sehen, den die Kachel nicht hat.
 *
 * 🔴 **Owner-Entscheid E8 (2026-09-18), W3 + W4.**
 *   - **W3 (Gewichtung):** die Warnfarbe geht an **„Deduction not determined"** —
 *     das ist die offene Arbeit, und `C_WARN` steht in dieser Datei sonst genau
 *     dafür (offener Betrag > 0). Die ZUGESAGTE Null ist eine getroffene
 *     Entscheidung und bekommt die gedämpfte Farbe. Vorher war es umgekehrt.
 *   - **W4 (Schreibweise):** `€0.00` statt `0,00 €`, wie die Oberfläche sonst
 *     überall (Geldkarte, Berichte). Die deutsche Form bleibt der Kundenmail
 *     und der deutschen Zeitleisten-Notiz. 🔴 **Seit PR 2 (2026-10-06) ist auch
 *     die Oberflächenform deutsch** — `formatMoney` → `0,00 €`; E8 ist für die
 *     Währungsstellung abgelöst. Die Trennung Kundenmail ↔ Oberfläche bleibt.
 */

import { formatMoney } from '../../utils/format'

// Der Platzhalter für einen Wert, den der Bericht nicht liefert. EINER für die
// ganze Kachel: ein fehlender Betrag ist „unbekannt", nie eine Null (Regel 22).
const PLATZHALTER = '—'

const euro = (wert) => (wert != null ? formatMoney(wert) : PLATZHALTER)
const anzahl = (wert) => (wert != null ? wert : PLATZHALTER)

/**
 * 🔴 N81 (Owner-Entscheid E5/2026-09-28, Plan §5.6 und §0.1 D8): vier Zeilen mehr.
 *   - „Collected from refunds" und „Recorded, not collected" zerlegen den Erlös-Topf:
 *     eingezogen wurde nur, wo es eine Erstattung gab, von der abgezogen werden konnte.
 *     Vorher lasen sich 44 Fälle / 285,60 € ohne Abzugsbasis dort als Erlös.
 *   - „Brewout trägt" ist eine ANZAHL, kein Betrag: auf einem Weg ohne Erstattung
 *     wird kein Eurobetrag gebucht.
 *   - die Schätzung heißt Schätzung zum KUNDENTARIF und ist keine Obergrenze; sie
 *     sagt dazu, wie oft sie ein Gewicht oder Land raten musste.
 * `defective_count` zählt seit N81 auch Fälle ohne Defekt (Brewout trägt das
 * Rückporto auf einer Lösung ohne Erstattung); die Beschriftung sagt deshalb, WER
 * zahlt, nicht warum.
 *
 * 🔴 **Regel-16-Gate, Runde 1 (B1, W1–W3, W8), nachgetragen 2026-09-29.** Vier
 * Befunde an derselben Kachel, alle an der Beschriftung und der Reihenfolge:
 *
 *   **B1** „Revenue from Shipping" beschriftete weiter den Topf, den N81 gerade
 *   als Nicht-Erlös zerlegt. Ein offener Kunde-zahlt-Fall steht dort ab der
 *   Anlage als „Erlös" (`_resolve_voluntary_return_defaults` setzt den Betrag,
 *   `refund_amount_eur` erst bei der Erstattung), und die zwei grünen
 *   Euro-Zahlen standen nebeneinander, ohne dass eine die andere erklärt. Jetzt
 *   heißt die Zeile **„Charged to customers (recorded)"** — was ANGESETZT ist,
 *   nicht was geflossen ist — und die zwei Euro-Zeilen stehen als **Teilzeilen**
 *   direkt darunter („of which …"). Der Kopf der Datei sagte die Trennung schon
 *   vorher; die Beschriftung sagte sie nicht.
 *
 *   **W2** „Brewout pays return shipping" (rot) und „Carried by us" (grau) waren
 *   dieselbe Tatsache unter zwei Namen, und die zweite stand zwischen Euro-Zeilen
 *   ohne „cases" — ein Leser addiert sie zu den vier Zählzeilen, die sich zur
 *   Gesamtzahl summieren. Die getragene Anzahl ist jetzt eine **Teilzeile** unter
 *   der roten Zeile, mit „cases" im Text, und die Kachel nennt den Träger
 *   durchgehend **„Brewout"**, nicht „us".
 *
 *   **W1** „(no refund)" ist für den laufenden Bestand nur „noch keine": der
 *   Betrag steht ab der Anlage, die Erstattung kommt später. Jetzt
 *   **„no refund recorded yet"**.
 *
 *   **W3** Der Zusatz der Schätzung brach die Zeile um (389 px bei ~327 px
 *   verfügbarer Breite bei 1100 px Viewport). Er ist jetzt eine **zweite,
 *   gedämpfte Zeile** (`optionen.hinweis`) statt an den Wert gehängt.
 *
 *   **W4** „with guessed weight" zählte auch ein fehlendes Land mit
 *   (`_rueckporto_getragen_schaetzung`: `gewicht_unbrauchbar(gewicht) or not land`).
 *   Jetzt **„with guessed weight or country"**.
 *
 *   **W8** Beide Abfragen filtern `case_type = 'return'`; 65 der 80 Fälle der vier
 *   Wege liegen auf service/inquiry. Der Hinweis „return cases only" steht an der
 *   Kachel (`RMAReports.jsx`), nicht in dieser Funktion — er beschreibt den
 *   Umfang, nicht eine Zeile.
 *
 * @param {object} sh  der `shipping`-Block aus `GET /reports/financial`
 * @param {object} farben  { gut, fehler, warn, gedaempft } — Tokenwerte des Aufrufers
 * @returns {Array<[string, (string|number), (string|undefined), (object|undefined)]>}
 *   `[Beschriftung, Wert, Farbe, optionen]`. `optionen.teilzeile` rückt die Zeile
 *   ein (sie gehört zur Zeile darüber), `optionen.hinweis` hängt ihr eine zweite,
 *   gedämpfte Zeile an. Beides ist Darstellung; die Aussage steht in der
 *   Beschriftung.
 */
export function versandkostenZeilen(sh, farben = {}) {
  const s = sh || {}
  const erlös = euro(s.customer_paid_eur)
  const schaetzung = s.brewout_carried_est_eur != null
    ? euro(s.brewout_carried_est_eur)
    : PLATZHALTER
  return [
    ['Total Return Cases', s.total_returns, undefined],
    ['Brewout pays return shipping', s.defective_count, farben.fehler],
    // W2: Teilzeile unter der roten Zeile, „cases" im Text, kein zweiter Name.
    ['of which: no refund on the resolution (cases)', anzahl(s.brewout_carried_count),
      farben.gedaempft, { teilzeile: true }],
    ['Customer Paid Shipping', s.customer_paid_count, farben.gut],
    // B1: „angesetzt", nicht „geflossen". Die zwei Teilzeilen darunter zerlegen sie.
    ['Charged to customers (recorded)', erlös, farben.gut],
    ['of which collected from refunds', euro(s.collected_eur), farben.gut,
      { teilzeile: true }],
    // W1: „noch keine" statt „keine".
    ['of which not collected (no refund recorded yet)', euro(s.uncollected_eur),
      farben.warn, { teilzeile: true }],
    // 🔴 Zwei Zeilen, zwei Aussagen. „Free return" ist die ZUGESAGTE Null
    // (Owner §E1); „Deduction not determined" ist die Zeile, bei der gerade
    // NICHT feststeht, dass wir etwas gewährt haben.
    ['Free Return (€0.00 promised)', s.free_return_count, farben.gedaempft],
    ['Deduction not determined', s.undetermined_count, farben.warn],
    ['Brewout carries, estimate at customer tariff', schaetzung, farben.gedaempft,
      // W3 + W4: zweite gedämpfte Zeile statt Umbruch im Wert.
      { hinweis: s.brewout_carried_est_weight_count
        ? `${s.brewout_carried_est_weight_count} with guessed weight or country`
        : null }],
  ]
}

export default versandkostenZeilen
